"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SseClient = void 0;
const error_1 = require("@engineering11/error");
const logger_1 = require("@engineering11/logger");
const sse_shared_api_1 = require("@engineering11/sse-shared-api");
const socket_io_client_1 = require("socket.io-client");
const constants_1 = require("../constants");
class SseClient {
    options;
    logger;
    socket; // TODO: Statically type events
    callbacks = new Map();
    constructor(options) {
        this.options = options;
        this.logger = new logger_1.E11Logger(SseClient.name, { suppressLogging: options.suppressLogging });
        this.socket = (0, socket_io_client_1.io)(options.endpoint ?? 'http://localhost:8080', { transports: ['websocket'] }); // TODO: Replace with actual default server location
        this.initCallbacks();
    }
    initCallbacks() {
        this.socket.on('connect', () => {
            this.logger.debug('Connected');
        });
        this.socket.on('event', async (event) => {
            const configs = this.callbacks.get(event.channelName);
            if (configs) {
                await Promise.all(configs.map(config => config.callback(event)));
            }
        });
        this.socket.on('system', async (message) => {
            if (message.code === sse_shared_api_1.SystemMessageCode.RemovedFromChannel) {
                this.logger.log(`Disconnected from channels ${message.data.channels}, attempting a reconnect....`);
                await Promise.all(message.data.channels.map((channelName) => this.reconnectToChannel(channelName)));
            }
        });
        this.socket.io.on('reconnect', async () => {
            await Promise.all(Array.from(this.callbacks.keys()).map(channelName => this.reconnectToChannel(channelName)));
        });
        this.socket.io.on('reconnect_attempt', data => {
            this.logger.log(`reconnect_attempt: `, data);
        });
        this.socket.on('disconnect', reason => {
            // Known reasons: "io server disconnect" | "io client disconnect" | "ping timeout" | "transport close" | "transport error" | "parse error"
            this.logger.log(`Disconnected. Reason: `, reason);
        });
    }
    async listen(channelName, callback, listenRequestOptions) {
        await this.connectToChannel(channelName, listenRequestOptions);
        const config = { callback, listenRequestOptions };
        const configs = this.callbacks.get(channelName);
        if (configs) {
            configs.push(config);
        }
        else {
            this.callbacks.set(channelName, [config]);
        }
    }
    /**
     * Removes a callback from a channel if one is provided.
     * Checks for memory equality so callback function will need to be saved in a separate variable
     * If no function is provided then it will delete all callbacks for the channel.
     *
     * If there are no callbacks left after the operation then a message will be sent to remove the client entirely.
     * @param channelName
     * @param callback
     * @returns
     */
    async removeListener(channelName, callback) {
        const channelCallbacks = this.callbacks.get(channelName);
        if (!channelCallbacks || channelCallbacks.length === 0) {
            return;
        }
        if (callback) {
            const newCallbacks = channelCallbacks.filter(callbackConfig => callbackConfig.callback !== callback);
            this.callbacks.set(channelName, newCallbacks);
            if (newCallbacks.length === 0) {
                await this.disconnectFromChannel(channelName);
            }
        }
        else {
            this.callbacks.delete(channelName);
            await this.disconnectFromChannel(channelName);
        }
    }
    async connectToChannel(channelName, listenRequestOptions) {
        const listenRequest = { channelName, projectId: this.options.projectId };
        if (listenRequestOptions?.auth) {
            let authKey;
            const extraArgs = listenRequestOptions.auth.args ?? [];
            if (typeof listenRequestOptions.auth.fetcher === 'function') {
                authKey = await listenRequestOptions.auth.fetcher(channelName, ...extraArgs);
            }
            else {
                authKey = await listenRequestOptions.auth.fetcher.fetchToken(channelName, ...extraArgs);
            }
            listenRequest.authKey = authKey;
        }
        try {
            const response = await this.socket.emitWithAck('listen', listenRequest);
            this.logger.log('Connected to channel: ', channelName);
            return;
        }
        catch (e) {
            throw (0, error_1.toE11Error)(e, {
                title: 'Connection failed',
                type: constants_1.SSE_CLIENT_ERRORS.LISTEN_FAILED,
                additionalData: { channelName },
            });
        }
    }
    async disconnectFromChannel(channelName) {
        const removeFromChannelRequest = { channelName, projectId: this.options.projectId };
        try {
            const response = await this.socket.emitWithAck('remove', removeFromChannelRequest);
            this.logger.log(`Removed from channel: ${channelName}`);
        }
        catch (err) {
            throw (0, error_1.toE11Error)(err, {
                title: 'Disconnect failed',
                type: constants_1.SSE_CLIENT_ERRORS.DISCONNECT_FAILED,
                additionalData: { channelName },
            });
        }
    }
    async reconnectToChannel(channelName) {
        const configs = this.callbacks.get(channelName);
        this.callbacks.set(channelName, []);
        for (const config of configs) {
            try {
                await this.listen(channelName, config.callback, config.listenRequestOptions);
            }
            catch (err) {
                this.logger.error(err);
            }
        }
    }
}
exports.SseClient = SseClient;
